/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.28/esri/copyright.txt for details.
*/
import { _ as e } from "../chunks/tslib.es6.js";
import t from "../core/Collection.js";
import { eventKey as i } from "../core/events.js";
import { on as s, watch as o, initial as n, syncAndInitial as l } from "../core/reactiveUtils.js";
import { property as r } from "../core/accessorSupport/decorators/property.js";
import { cast as a } from "../core/accessorSupport/decorators/cast.js";
import "../core/arrayUtils.js";
import "../core/has.js";
import { subclass as c } from "../core/accessorSupport/decorators/subclass.js";
import d from "./Widget.js";
import h from "./LayerList/LayerListViewModel.js";
import u from "./LayerList/ListItem.js";
import { findSelectedItem as m, sortLayersToIds as g, sortChildLayersToIds as p, canSortSublayers as _ } from "./LayerList/support/layerListUtils.js";
import { loadCalciteComponents as b } from "./support/componentsUtils.js";
import { globalCss as y } from "./support/globalCss.js";
import { legacyIcon as I } from "./support/legacyIcon.js";
import { accessibleHandler as v } from "./support/decorators/accessibleHandler.js";
import { messageBundle as f } from "./support/decorators/messageBundle.js";
import { vmEvent as C } from "./support/decorators/vmEvent.js";
import { tsx as S } from "./support/jsxFactory.js";
import "./support/widgetUtils.js";
import A from "sortablejs";
function $(e, t, i) {
  e.splice(i, 0, e.splice(t, 1)[0]);
}
const w = t.ofType(u),
  k = "data-sort-filter",
  M = "data-layer-uid",
  T = "layerUid",
  E = "esri-layer-list",
  L = `${E}__item`,
  x = {
    base: E,
    widgetIcon: I.layers,
    newUI: `${E}--new-ui`,
    noItems: `${E}__no-items`,
    list: `${E}__list`,
    listRoot: `${E}__list--root`,
    listExclusive: `${E}__list--exclusive`,
    listEmpty: `${E}__list--empty`,
    listEmptyState: `${E}__list-empty-state`,
    listEmptyStateDragEnabled: `${E}__list-empty-state--drag-enabled`,
    listInherited: `${E}__list--inherited`,
    listIndependent: `${E}__list--independent`,
    item: L,
    itemContent: `${L}-content`,
    itemMessage: `${L}--has-message`,
    itemInvisible: `${L}--invisible`,
    itemInvisibleAtScale: `${L}--invisible-at-scale`,
    itemChildren: `${L}--has-children`,
    itemSelectable: `${L}--selectable`,
    itemContainer: `${L}-container`,
    actionsMenu: `${L}-actions-menu`,
    actionsMenuItem: `${L}-actions-menu-item`,
    actionsMenuItemActive: `${L}-actions-menu-item--active`,
    actions: `${L}-actions`,
    actionsList: `${L}-actions-list`,
    action: `${L}-action`,
    actionIcon: `${L}-action-icon`,
    actionImage: `${L}-action-image`,
    actionTitle: `${L}-action-title`,
    actionToggle: `${E}__action-toggle`,
    actionToggleOn: `${E}__action-toggle--on`,
    label: `${L}-label`,
    message: `${L}-message`,
    title: `${L}-title`,
    statusIndicator: `${E}__status-indicator`,
    publishing: `${E}__publishing`,
    updating: `${E}__updating`,
    connectionStatus: `${E}__connection-status`,
    connectionStatusConnected: `${E}__connection-status--connected`,
    toggleVisible: `${L}-toggle`,
    toggleVisibleIcon: `${L}-toggle-icon`,
    toggleIcon: `${L}-toggle-icon`,
    radioIcon: `${L}-radio-icon`,
    childToggle: `${E}__child-toggle`,
    childToggleOpen: `${E}__child-toggle--open`,
    childOpened: `${E}__child-toggle-icon--opened`,
    childClosed: `${E}__child-toggle-icon--closed`,
    childClosed_RTL: `${E}__child-toggle-icon--closed-rtl`,
    sortableChosen: `${E}--chosen`
  },
  R = {
    actions: "actions",
    actionSection: "action-section",
    items: "items"
  },
  N = {
    exclusive: "exclusive",
    inherited: "inherited",
    independent: "independent"
  };
function j(e) {
  const {
    actionsOpen: t,
    children: i
  } = e;
  t && (e.actionsOpen = !1), i.forEach(e => j(e));
}
const O = "root",
  U = {
    statusIndicators: !0,
    errors: !1
  };
let V = class extends d {
  constructor(e, t) {
    super(e, t), this._sortableNodes = new Map(), this._sortableMap = new Map(), this._focusSortUid = null, this._tooltipReferenceMap = new Map(), this.visibleItems = null, this.iconClass = x.widgetIcon, this.icon = null, this.listItemCanGiveFunction = null, this.listItemCanReceiveFunction = null, this.messages = null, this.messagesCommon = null, this.multipleSelectionEnabled = !1, this.selectionEnabled = !1, this.selectedItems = new w(), this.style = "classic", this.viewModel = new h(), this.visibleElements = {
      ...U
    }, this._onSortableSort = ({
      to: e,
      from: t,
      item: i,
      newIndex: s
    }) => {
      t && e && (t === e ? this._sortLayers(this._sortableMap.get(t.dataset.group)) : this._moveLayerFromChildList({
        to: e,
        from: t,
        item: i,
        newIndex: s
      }));
    }, this._sortableCanSort = (e, t) => !(!e.el.dataset.group || !t.el.dataset.group), this._sortableCanPull = (e, t, i) => {
      const {
          listItemCanGiveFunction: s
        } = this,
        o = {
          selected: P(i),
          from: P(t.el),
          to: P(e.el)
        };
      return !this._sortableCanSort(e, t) || "function" != typeof s || s.call(null, o);
    }, this._sortableCanPut = (e, t, i) => {
      const {
          listItemCanReceiveFunction: s
        } = this,
        o = {
          selected: P(i),
          from: P(t.el),
          to: P(e.el)
        };
      return !this._sortableCanSort(e, t) || "function" != typeof s || s.call(null, o);
    }, this._onSortableEnd = ({
      oldIndex: e,
      from: t,
      to: i,
      item: s
    }) => {
      t !== i && t.insertBefore(s, t.children[e]);
    };
  }
  initialize() {
    const e = this.operationalItems;
    this._setVisibleItems(e), this.addHandles([s(() => this.operationalItems, "change", () => this._itemsChanged(e)), o(() => this.visibleElements, () => this._itemsChanged(e)), o(() => this.selectionEnabled, () => this._toggleAllSorting(), n)]);
  }
  loadDependencies() {
    return b({
      icon: () => import("@esri/calcite-components/dist/components/calcite-icon.js"),
      tooltip: () => import("@esri/calcite-components/dist/components/calcite-tooltip.js")
    });
  }
  destroy() {
    this._destroySortables(), this._tooltipReferenceMap.clear();
  }
  get label() {
    return this.messages?.widgetLabel ?? "";
  }
  set label(e) {
    this._overrideIfSome("label", e);
  }
  get listItemCreatedFunction() {
    return this.viewModel.listItemCreatedFunction;
  }
  set listItemCreatedFunction(e) {
    this.viewModel.listItemCreatedFunction = e;
  }
  get operationalItems() {
    return this.viewModel.operationalItems;
  }
  set operationalItems(e) {
    this.viewModel.operationalItems = e;
  }
  get view() {
    return this.viewModel.view;
  }
  set view(e) {
    this.viewModel.view = e;
  }
  castVisibleElements(e) {
    return {
      ...U,
      ...e
    };
  }
  triggerAction(e, t) {
    return this.viewModel.triggerAction(e, t);
  }
  render() {
    const {
        visibleItems: e,
        style: t
      } = this,
      i = this.viewModel?.state,
      s = {
        [x.newUI]: "modern" === t,
        [y.hidden]: "loading" === i,
        [y.disabled]: "disabled" === i
      },
      o = this._renderItemTooltips(),
      n = this._renderItems();
    return S("div", {
      class: this.classes(x.base, y.widget, y.panel, s)
    }, e.length ? [o, n] : this._renderNoItems());
  }
  _renderItemTooltip(e) {
    const {
      _tooltipReferenceMap: t,
      messages: i
    } = this;
    return e ? S("calcite-tooltip", {
      referenceElement: t.get(e.uid)
    }, i.layerIncompatibleTooltip) : null;
  }
  _renderItemTooltipNodes(e) {
    const {
      incompatible: t,
      children: i
    } = e;
    return [t ? this._renderItemTooltip(e) : null, ...(!t && i ? i.toArray().map(e => this._renderItemTooltipNodes(e)) : [])];
  }
  _renderItemTooltips() {
    return this.visibleItems.toArray().map(e => this._renderItemTooltipNodes(e)) ?? [];
  }
  _renderNoItems() {
    return S("div", {
      class: x.noItems
    }, this.messages.noItemsToDisplay);
  }
  _renderItems() {
    const {
      visibleItems: e,
      selectionEnabled: t,
      messages: i
    } = this;
    return S("ul", {
      afterCreate: this._sortNodeCreated,
      afterRemoved: this._sortNodeRemoved,
      afterUpdate: this._sortNodeCreated,
      "aria-label": i.widgetLabel,
      bind: this,
      class: this.classes(x.list, x.listRoot, x.listIndependent),
      "data-group": O,
      role: t ? "listbox" : void 0
    }, e.map(e => this._renderItem(e, null)).toArray());
  }
  _renderActionsMenuIcon(e, t) {
    const {
        messagesCommon: i
      } = this,
      s = {
        [x.actionsMenuItemActive]: e.actionsOpen
      };
    return S("div", {
      "aria-controls": t,
      "aria-label": i.options,
      bind: this,
      class: this.classes(x.actionsMenuItem, s),
      "data-item": e,
      key: "actions-menu-toggle",
      onclick: this._toggleActionsOpen,
      onkeydown: this._toggleActionsOpen,
      role: "button",
      tabIndex: 0,
      title: i.options
    }, S("span", {
      "aria-hidden": "true",
      class: I.handleHorizontal
    }));
  }
  _renderActionsMenu(e, t, i, s) {
    const {
        panel: o
      } = e,
      n = o && o.visible ? this._renderPanelButton(o) : null,
      l = 1 === i && this._getSingleActionButton(t),
      r = l ? this._renderAction({
        item: e,
        action: l,
        singleAction: !0
      }) : null,
      a = !l && i ? this._renderActionsMenuIcon(e, s) : null;
    return a || n || l ? S("div", {
      class: x.actionsMenu,
      key: "actions-menu"
    }, n, r, a) : null;
  }
  _renderChildList(e, t) {
    const {
        selectionEnabled: i
      } = this,
      {
        visibilityMode: s,
        children: o
      } = e,
      n = this._hasChildren(e),
      l = !n && i && "group" === e.layer?.type,
      {
        exclusive: r,
        inherited: a
      } = N,
      c = {
        [x.listEmpty]: !n,
        [x.listExclusive]: s === r,
        [x.listInherited]: s === a,
        [x.listIndependent]: s !== a && s !== r
      },
      d = {
        [x.listEmptyState]: !0,
        [x.listEmptyStateDragEnabled]: i
      };
    return n || l ? n ? S("ul", {
      afterCreate: this._sortNodeCreated,
      afterRemoved: this._sortNodeRemoved,
      afterUpdate: this._sortNodeCreated,
      "aria-expanded": e.open ? "true" : "false",
      bind: this,
      class: this.classes(x.list, c),
      "data-group": e.uid,
      "data-item": e,
      hidden: !e.open && !l || null,
      id: t,
      key: "list-items",
      role: i ? "listbox" : s === r ? "radiogroup" : "group"
    }, o?.map(t => this._renderItem(t, e)).toArray()) : S("div", {
      afterCreate: this._sortNodeCreated,
      afterRemoved: this._sortNodeRemoved,
      afterUpdate: this._sortNodeCreated,
      bind: this,
      class: this.classes(d),
      "data-group": e.uid,
      "data-item": e,
      id: t,
      key: "list-items"
    }, this.messages.noItemsToDisplay) : null;
  }
  _renderChildrenToggle(e, t) {
    const {
        messagesCommon: i
      } = this,
      s = this._hasChildren(e),
      o = {
        [x.childToggleOpen]: e.open
      },
      n = e.open ? i.collapse : i.expand;
    return s ? S("span", {
      "aria-controls": t,
      "aria-label": n,
      class: this.classes(x.childToggle, o),
      "data-item": e,
      key: "toggle-children",
      onclick: this._toggleChildrenClick,
      onkeydown: this._toggleChildrenClick,
      role: "button",
      tabIndex: 0,
      title: n
    }, S("span", {
      "aria-hidden": "true",
      class: this.classes(x.childClosed, I.rightTriangleArrow)
    }), S("span", {
      "aria-hidden": "true",
      class: this.classes(x.childOpened, I.downArrow)
    }), S("span", {
      "aria-hidden": "true",
      class: this.classes(x.childClosed_RTL, I.leftTriangleArrow)
    })) : null;
  }
  _renderItemMessage(e) {
    return e.error ? S("div", {
      class: x.message,
      key: "esri-layer-list__error",
      role: "alert"
    }, S("span", {
      "aria-hidden": "true",
      class: I.noticeTriangle
    }), this.messages.layerError) : e.incompatible ? S("div", {
      class: x.message,
      key: "esri-layer-list__incompatible",
      role: "alert"
    }, S("span", {
      afterCreate: t => this._setTooltipReference(t, e),
      "aria-hidden": "true",
      bind: this,
      class: I.noticeTriangle,
      tabIndex: 0
    }), this.messages.layerIncompatible) : null;
  }
  _renderItemContent(e, t, i) {
    const {
        id: s
      } = this,
      o = `${s}_${e.uid}`,
      n = `${o}_actions`,
      l = `${o}__list`,
      {
        panel: r
      } = e,
      a = this._filterActions(e.actionsSections),
      c = this._countActions(a);
    return [S("div", {
      class: x.itemContainer,
      key: "list-item-container"
    }, this._renderChildrenToggle(e, l), this._renderLabel(e, t, i), this._renderActionsMenu(e, a, c, n)), this._renderItemMessage(e), c ? this._renderActionsSections(e, a, n) : null, r && r.open && !r.disabled ? r.render() : null, this._renderChildList(e, l)];
  }
  _renderItem(e, t) {
    const {
        style: i,
        id: s,
        selectionEnabled: o,
        selectedItems: n
      } = this,
      l = `${`${s}_${e.uid}`}__title`,
      r = this._hasMessage(e),
      a = this._hasChildren(e),
      c = {
        [x.itemChildren]: a,
        [x.itemMessage]: !!r,
        [x.itemInvisible]: "modern" === i && !e.visible,
        [x.itemInvisibleAtScale]: !e.visibleAtCurrentScale,
        [x.itemSelectable]: o
      };
    if (o) {
      const i = {
        [M]: e.layer?.uid,
        [k]: (!e.sortable).toString()
      };
      return S("li", {
        afterCreate: this._focusListItem,
        afterUpdate: this._focusListItem,
        "aria-labelledby": l,
        "aria-selected": m(e, n) ? "true" : "false",
        bind: this,
        class: this.classes(x.item, c),
        "data-group": t ? t.uid : O,
        "data-item": e,
        key: `item-with-selection-${e.uid}`,
        onclick: this._toggleSelection,
        onkeydown: this._selectionKeydown,
        role: "option",
        tabIndex: 0,
        ...i
      }, this._renderItemContent(e, t, l));
    }
    return S("li", {
      afterCreate: this._focusListItem,
      afterUpdate: this._focusListItem,
      "aria-labelledby": l,
      bind: this,
      class: this.classes(x.item, c),
      key: `item-no-selection-${e.uid}`
    }, this._renderItemContent(e, t, l));
  }
  _renderConnectionIcon(e) {
    const {
      connectionStatus: t
    } = e;
    return null != t ? S("calcite-icon", {
      icon: "connected" === t ? "beacon" : "offline",
      scale: "s"
    }) : null;
  }
  _renderItemStatus(e, t) {
    const {
      visibleElements: i
    } = this;
    if (!i.statusIndicators) return null;
    const {
        connectionStatus: s,
        publishing: o,
        updating: n
      } = e,
      l = n && !t,
      r = !!s;
    return S("span", {
      class: this.classes({
        [x.statusIndicator]: !0,
        [x.publishing]: o,
        [x.updating]: l,
        [x.connectionStatus]: r,
        [x.connectionStatusConnected]: r && "connected" === s
      }),
      key: "layer-item-status"
    }, this._renderConnectionIcon(e));
  }
  _renderItemTitle(e, t) {
    const {
      messages: i
    } = this;
    if (!e) return null;
    const s = e.title || i.untitledLayer,
      o = e.visibleAtCurrentScale ? s : `${s} (${i.layerInvisibleAtScale})`;
    return S("span", {
      "aria-label": o,
      class: x.title,
      id: t,
      key: "layer-title-container",
      title: o
    }, s);
  }
  _renderItemToggleIcon({
    visible: e
  }, t) {
    const {
        style: i
      } = this,
      {
        exclusive: s
      } = N,
      o = t?.visibilityMode,
      n = "modern" === i,
      l = o === s,
      r = {
        [x.toggleVisibleIcon]: n,
        [x.toggleIcon]: n && !l,
        [x.radioIcon]: n && l,
        [I.radioChecked]: l && e,
        [I.radioUnchecked]: l && !e,
        [I.visible]: !l && e,
        [I.nonVisible]: !l && !e
      };
    return S("span", {
      "aria-hidden": "true",
      class: this.classes(r),
      key: "item-toggle-icon"
    });
  }
  _renderItemToggle(e, t, i) {
    const {
        selectionEnabled: s,
        messages: o
      } = this,
      {
        exclusive: n
      } = N,
      l = t?.visibilityMode,
      r = l === n ? "radio" : "switch";
    return S("span", s ? {
      "aria-checked": e.visible ? "true" : "false",
      "aria-labelledby": i,
      bind: this,
      class: x.toggleVisible,
      "data-item": e,
      "data-parent-visibility": l,
      key: "item-toggle-selection-enabled",
      onclick: this._toggleVisibility,
      onkeydown: this._toggleVisibility,
      role: r,
      tabIndex: 0,
      title: o.layerVisibility
    } : {
      class: x.toggleVisible,
      key: "item-toggle"
    }, this._renderItemToggleIcon(e, t));
  }
  _renderLabel(e, t, i) {
    const {
        selectionEnabled: s,
        style: o,
        messages: n
      } = this,
      {
        inherited: l,
        exclusive: r
      } = N,
      a = t?.visibilityMode,
      c = a === r ? "radio" : "switch",
      d = this._renderItemTitle(e, i),
      h = this._renderItemStatus(e, t),
      u = this._renderItemToggle(e, t, i),
      m = "modern" === o ? [d, h, u] : [u, d, h],
      g = S("div", s ? {
        class: x.label,
        key: `item-label-no-selection-${e.uid}`
      } : {
        "aria-checked": e.visible ? "true" : "false",
        "aria-labelledby": i,
        bind: this,
        class: x.label,
        "data-item": e,
        "data-parent-visibility": a,
        key: `item-label-with-selection-${e.uid}`,
        onclick: this._toggleVisibility,
        onkeydown: this._toggleVisibility,
        role: c,
        tabIndex: 0,
        title: n.layerVisibility
      }, m);
    return a === l || e.error ? S("div", {
      class: x.label,
      key: `item-label-container-${e.uid}`
    }, this._renderItemTitle(e, i), this._renderItemStatus(e, t)) : g;
  }
  _renderPanelButton(e) {
    const {
        className: t,
        open: i,
        title: s,
        image: o,
        disabled: n
      } = e,
      l = o || t ? t : I.defaultAction,
      r = this._getIconImageStyles(e),
      a = {
        [x.actionsMenuItemActive]: i && !n
      },
      c = {
        [x.actionImage]: !!r["background-image"],
        [y.disabledElement]: e.disabled
      };
    return l && (c[l] = !!l), S("div", {
      "aria-disabled": e.disabled.toString(),
      "aria-label": s,
      bind: this,
      class: this.classes(x.actionsMenuItem, a),
      "data-panel": e,
      key: `panel-${e.uid}`,
      onclick: this._triggerPanel,
      onkeydown: this._triggerPanel,
      role: "button",
      tabIndex: e.disabled ? void 0 : 0,
      title: s
    }, S("span", {
      class: this.classes(c),
      styles: r
    }));
  }
  _renderActionsSections(e, t, i) {
    const s = t.toArray().map((t, i) => S("ul", {
      class: x.actionsList,
      key: `${e}-action-section-${i}`
    }, this._renderActionSection(e, t)));
    return S("div", {
      "aria-expanded": e.actionsOpen ? "true" : "false",
      class: x.actions,
      hidden: !e.actionsOpen || null,
      id: i,
      key: "actions-section",
      role: "group"
    }, s);
  }
  _renderActionSection(e, t) {
    return (t && t.toArray()).map(t => this._renderAction({
      item: e,
      action: t
    }));
  }
  _renderActionIcon(e) {
    const {
        active: t,
        className: i
      } = e,
      s = this._getIconImageStyles(e),
      o = "button" !== e.type || e.image || i ? i : I.defaultAction,
      n = {
        [x.actionImage]: !t && !!s["background-image"],
        [I.loadingIndicator]: t,
        [y.rotating]: t
      };
    return o && !t && (n[o] = !0), S("span", {
      "aria-hidden": "true",
      class: this.classes(x.actionIcon, n),
      key: "action-icon",
      styles: s
    });
  }
  _renderActionTitle(e, t) {
    return t ? null : S("span", {
      class: x.actionTitle,
      key: "action-title"
    }, e);
  }
  _renderAction(e) {
    const {
        item: t,
        action: i,
        singleAction: s
      } = e,
      {
        active: o,
        disabled: n,
        title: l
      } = i,
      r = {
        [x.actionsMenuItem]: s && "button" === i.type,
        [x.action]: o || !s && "toggle" !== i.type,
        [x.actionToggle]: !o && "toggle" === i.type,
        [x.actionToggleOn]: !o && "toggle" === i.type && i.value,
        [y.disabledElement]: n
      },
      a = [this._renderActionIcon(i), this._renderActionTitle(l, s)];
    return s ? S("div", {
      "aria-label": l ?? void 0,
      bind: this,
      classes: r,
      "data-action": i,
      "data-item": t,
      key: `single-action-${i.uid}`,
      onclick: this._triggerAction,
      onkeydown: this._triggerAction,
      role: "button",
      tabIndex: 0,
      title: l ?? void 0
    }, a) : S("li", {
      "aria-label": l ?? void 0,
      bind: this,
      classes: r,
      "data-action": i,
      "data-item": t,
      key: `action-${i.uid}`,
      onclick: this._triggerAction,
      onkeydown: this._triggerAction,
      role: "button",
      tabIndex: 0,
      title: l ?? void 0
    }, a);
  }
  _setTooltipReference(e, t) {
    this._tooltipReferenceMap.set(t.uid, e), this.scheduleRender();
  }
  _hasMessage(e) {
    return !!e.error || e.incompatible;
  }
  _hasChildren(e) {
    return !!e.children.length && !this._hasMessage(e);
  }
  _sortNodeRemoved(e) {
    const {
        _sortableMap: t
      } = this,
      i = e.dataset.group,
      s = t.get(i);
    s && s.destroy(), t.delete(i);
  }
  _destroySortables() {
    const {
      _sortableMap: e,
      _sortableNodes: t
    } = this;
    e.forEach(e => e && e.destroy()), e.clear(), t.clear();
  }
  _moveLayerFromChildList({
    to: e,
    from: t,
    item: i,
    newIndex: s
  }) {
    const o = P(i),
      n = P(e),
      l = P(t);
    this.viewModel.moveListItem(o, l, n, s);
  }
  _sortLayers(e) {
    if (!e) return;
    const t = P(e.el),
      i = e.toArray();
    t ? p(t, i) : g(this.view?.map?.layers, i);
  }
  _toggleSorting(e, t) {
    const {
        _sortableMap: i,
        selectionEnabled: s
      } = this,
      o = i.get(t),
      n = P(e),
      l = (t === O || n?.childrenSortable && _(n)) && s;
    if (o) o.option("disabled", !l);else if (l) {
      const s = A.create(e, {
        dataIdAttr: M,
        group: {
          name: t,
          pull: this._sortableCanPull,
          put: this._sortableCanPut
        },
        filter: `[${k}="true"]`,
        fallbackTolerance: 4,
        onSort: this._onSortableSort,
        onEnd: this._onSortableEnd,
        disabled: !l,
        chosenClass: x.sortableChosen
      });
      i.set(t, s);
    }
  }
  _toggleAllSorting() {
    this._sortableNodes.forEach((e, t) => this._toggleSorting(e, t));
  }
  _sortNodeCreated(e) {
    const t = e.dataset.group;
    t && (this._sortableNodes.set(t, e), this._toggleSorting(e, t));
  }
  _setVisibleItems(e) {
    this.visibleItems = e.filter(e => !e.hidden && (this.visibleElements.errors || !e.error));
  }
  _getSingleActionButton(e) {
    return e.reduce(e => e).filter(e => e && "button" === e.type).at(0);
  }
  _focusListItem(e) {
    const {
      _focusSortUid: t
    } = this;
    if (!e || !t) return;
    const i = P(e);
    i?.layer?.uid === t && (e.focus(), this._focusSortUid = null);
  }
  _watchActionSectionChanges(e) {
    this.addHandles(e.on("change", this.scheduleRender.bind(this)), R.actionSection), e.forEach(e => this._renderOnActionChanges(e));
  }
  _renderOnActionChanges(e) {
    "toggle" !== e.type ? "slider" !== e.type ? this.addHandles(o(() => [e?.className, e?.image, e?.id, e?.title, e?.visible], () => this.scheduleRender(), n), R.actions) : this.addHandles(o(() => [e?.className, e?.id, e?.title, e?.visible, e?.value, e?.displayValueEnabled, e?.max, e?.min, e?.step], () => this.scheduleRender(), n), R.actions) : this.addHandles(o(() => [e?.className, e?.image, e?.id, e?.title, e?.visible, e?.value], () => this.scheduleRender(), n), R.actions);
  }
  _renderOnItemChanges(e) {
    this.addHandles([o(() => [e?.actionsOpen, e?.visible, e?.open, e?.updating, e?.connectionStatus, e?.publishing, e?.title, e?.visibleAtCurrentScale, e?.error, e?.visibilityMode, e?.panel, e?.panel?.title, e?.panel?.content, e?.panel?.className, e?.sortable, e?.childrenSortable], () => this.scheduleRender(), l), o(() => [e?.hidden, e?.error], () => this._setVisibleItems(this.operationalItems)), e.actionsSections.on("change", () => this.scheduleRender()), e.children.on("change", () => this.scheduleRender())], R.items), e.children.forEach(e => this._renderOnItemChanges(e)), e.actionsSections.forEach(e => this._watchActionSectionChanges(e));
  }
  _itemsChanged(e) {
    this.removeHandles(R.items), this.removeHandles(R.actions), this.removeHandles(R.actionSection), e.forEach(e => this._renderOnItemChanges(e)), this._tooltipReferenceMap.clear(), this._setVisibleItems(e), this.scheduleRender();
  }
  _filterActions(e) {
    return e.map(e => e.filter(e => e.visible));
  }
  _countActions(e) {
    return e.reduce((e, t) => e + t.length, 0);
  }
  _getIconImageStyles(e) {
    const t = "esri.widgets.LayerList.ListItemPanel" === e.declaredClass || "esri.support.Action.ActionButton" === e.declaredClass || "esri.support.Action.ActionToggle" === e.declaredClass ? e.image : null;
    return {
      "background-image": t ? `url("${t}")` : void 0
    };
  }
  _selectionKeydown(e) {
    const t = ["ArrowDown", "ArrowUp"],
      s = i(e);
    if (!t.includes(s)) return void this._toggleSelection(e);
    e.stopPropagation();
    const o = e.currentTarget,
      n = P(o),
      {
        _sortableMap: l,
        selectedItems: r
      } = this,
      a = o.dataset.group,
      c = l.get(a);
    if (!c || !n) return;
    const d = m(n, r),
      h = c.toArray(),
      u = e.target,
      g = h.indexOf(u.dataset[T]);
    if (-1 !== g) {
      if ("ArrowDown" === s) {
        const e = g + 1;
        if (e >= h.length) return;
        d ? ($(h, g, e), c.sort(h), this._sortLayers(c), this._focusSortUid = n.layer?.uid) : (this._focusSortUid = n.layer?.uid, this.scheduleRender());
      }
      if ("ArrowUp" === s) {
        const e = g - 1;
        if (e <= -1) return;
        d ? ($(h, g, e), c.sort(h), this._sortLayers(c), this._focusSortUid = n.layer?.uid) : (this._focusSortUid = n.layer?.uid, this.scheduleRender());
      }
    }
  }
  _toggleActionsOpen(e) {
    const t = P(e.currentTarget);
    if (!t) return;
    const {
        actionsOpen: i
      } = t,
      s = !i;
    s && this.operationalItems.forEach(e => j(e)), t.actionsOpen = s, e.stopPropagation();
  }
  _triggerPanel(e) {
    const t = F(e.currentTarget);
    e.stopPropagation(), t && !t.disabled && (t.open = !t.open);
  }
  _triggerAction(e) {
    const t = e.currentTarget,
      i = H(t),
      s = P(t);
    i && s && ("toggle" === i.type && (i.value = !i.value), this.triggerAction(i, s), e.stopPropagation());
  }
  _toggleVisibility(e) {
    const t = e.currentTarget,
      i = t.getAttribute("data-parent-visibility"),
      s = P(t);
    s && (i === N.exclusive && s.visible || (s.visible = !s.visible), e.stopPropagation());
  }
  _toggleChildrenClick(e) {
    const t = P(e.currentTarget);
    t && (t.open = !t.open, e.stopPropagation());
  }
  _toggleSelection(e) {
    e.stopPropagation();
    const {
        multipleSelectionEnabled: t,
        selectedItems: i
      } = this,
      s = P(e.currentTarget);
    if (!s) return;
    const o = m(s, i),
      {
        length: n
      } = i;
    if (!t) return n && !(o && 1 === n) ? (i.removeAll(), void i.add(s)) : void (o ? i.remove?.(o) : i.add(s));
    o ? i.remove?.(o) : i.add(s);
  }
  get test() {
    return {
      visibleItems: this.visibleItems
    };
  }
};
function P(e) {
  return e?.["data-item"];
}
function F(e) {
  return e?.["data-panel"];
}
function H(e) {
  return e?.["data-action"];
}
e([r()], V.prototype, "visibleItems", void 0), e([r()], V.prototype, "iconClass", void 0), e([r()], V.prototype, "icon", void 0), e([r()], V.prototype, "label", null), e([r()], V.prototype, "listItemCanGiveFunction", void 0), e([r()], V.prototype, "listItemCanReceiveFunction", void 0), e([r()], V.prototype, "listItemCreatedFunction", null), e([r(), f("esri/widgets/LayerList/t9n/LayerList")], V.prototype, "messages", void 0), e([r(), f("esri/t9n/common")], V.prototype, "messagesCommon", void 0), e([r()], V.prototype, "multipleSelectionEnabled", void 0), e([r()], V.prototype, "operationalItems", null), e([r()], V.prototype, "selectionEnabled", void 0), e([r()], V.prototype, "selectedItems", void 0), e([r()], V.prototype, "style", void 0), e([r()], V.prototype, "view", null), e([C("trigger-action"), r({
  type: h
})], V.prototype, "viewModel", void 0), e([r()], V.prototype, "visibleElements", void 0), e([a("visibleElements")], V.prototype, "castVisibleElements", null), e([v()], V.prototype, "_toggleActionsOpen", null), e([v()], V.prototype, "_triggerPanel", null), e([v()], V.prototype, "_triggerAction", null), e([v()], V.prototype, "_toggleVisibility", null), e([v()], V.prototype, "_toggleChildrenClick", null), e([v()], V.prototype, "_toggleSelection", null), V = e([c("esri.widgets.LayerList")], V);
const B = V;
export { B as default };